import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setCurrentSubStep } from '../../store/rfqSlice';
import { RFQ } from '../../types';
import ActionErrorToast from '../common/ActionErrorToast';
import { SUB_STEP_IDS } from '../../pages/wizard/journeyModel';
import ContextStepPanel from '../../pages/wizard/context/ContextStepPanel';
import { toContextQuestions } from '../../pages/wizard/context/contextQuestions';
import ValidationStep from '../../pages/wizard/validation/ValidationStep';
import AnalysisWait from '../../pages/wizard/context/AnalysisWait';
import { useAnalysisRetry } from '../../hooks/useAnalysisRetry';
import { useLongWait } from '../../hooks/useLongWait';
import { useBomAnalysisRun } from '../../hooks/useBomAnalysisRun';
import { useBomFixActions } from '../../hooks/useBomFixActions';
import { useBomStageReport } from '../../hooks/useBomStageReport';
import { useDiscoveryActions } from '../../hooks/useDiscoveryActions';
import { analysisCopy } from '../../utils/waitCopy';
import SupplierDiscovery from '../robotics/SupplierDiscovery';
import { StageReport } from '../../utils/stageActivity';
import { useReadOnly } from '../../contexts/WizardAccessContext';
import { useEndUserHandoff } from '../../hooks/useEndUserHandoff';
import SmartBomDiscoveryPanel from '../bom/smartbom/SmartBomDiscoveryPanel';
import SmartBomCheckpoint from '../bom/smartbom/SmartBomCheckpoint';

interface Step2SmartBOMReviewProps {
  rfq: RFQ;
  onNext: () => void;
  onPrevious: () => void;
  /** Prefills Robbie's docked input from step content ("Why these suppliers?"). */
  onAskRobbie?: (text: string) => void;
  /** Tells the wizard this step is mid-run, so Robbie drops the suggestion. */
  onWorking?: (stage: StageReport | null) => void;
}

const Step2SmartBOMReview: React.FC<Step2SmartBOMReviewProps> = ({
  rfq,
  onNext,
  onPrevious,
  onAskRobbie,
  onWorking,
}) => {
  const dispatch = useDispatch();
  const currentSubStep = useSelector((state: RootState) => state.rfq.currentSubStep);
  const readOnly = useReadOnly();
  const run = useBomAnalysisRun(rfq);
  const { rfqData, bomId, components, research, analysisData, isAnalyzing, analysisFailed } = run;
  const fixes = useBomFixActions(run);
  const discovery = useDiscoveryActions(rfq, run);
  const [validationRunning, setValidationRunning] = useState(false);
  const [validationFailed, setValidationFailed] = useState(false);

  // End-user handoff on the technical sub-steps (context + validation).
  // Share/sign-off refetch in place so banners flip without moving the user.
  const handoff = useEndUserHandoff(rfq, () => {
    void run.refreshRfq();
  });

  const contextQuestions = useMemo(() => toContextQuestions(analysisData), [analysisData]);
  // What the BOM already answered and the rows sourced as exact parts — read
  // instead of asked; runs analysed before per-row questions have neither.
  const bomAnswers = useMemo(() => analysisData?.bomAnswers || [], [analysisData]);
  const exactParts = useMemo(() => analysisData?.contextFromBom?.exactParts || [], [analysisData]);

  // The analysis run is persisted as one unit (mapping + context detection
  // land together), so it is either not started or finished; the /status
  // counters describe the later research phase and must not drive this bar.
  const analysisComponentCount = analysisData?.mappingSummary?.total || components.length;
  const analysisRunProgress = {
    completed: analysisData ? analysisComponentCount : 0,
    total: analysisComponentCount,
  };

  // Discovery (3c) comes after the analysis, so it is never waiting on one.
  // Coming back to an RFQ mid-research, the BOM still reads "processing" and
  // the first poll has not answered yet: without this the analysis overlay
  // flashed where the research popup belongs.
  const onDiscovery = currentSubStep === 3;
  const analysisRunning = isAnalyzing && !analysisFailed && !onDiscovery;
  const analysisLong = useLongWait(analysisRunning, analysisCopy.longAfterMs(analysisComponentCount), run.analysisStartedAt);
  const analysisRetry = useAnalysisRetry(bomId, run.restartAnalysis);
  const analysisWait = (
    <AnalysisWait
      running={!analysisFailed}
      failed={analysisFailed}
      components={analysisComponentCount}
      long={analysisLong}
      onRetry={readOnly ? undefined : analysisRetry.retry}
      retryError={analysisRetry.retryError}
    />
  );
  useBomStageReport(onWorking, {
    subStep: currentSubStep,
    analysisRunning,
    analysisFailed: analysisFailed && !onDiscovery,
    analysisComponents: analysisComponentCount,
    validationRunning,
    validationFailed,
    startingResearch: discovery.isStartingResearch,
    research,
    components,
  });

  // The analysis wait covers whichever sub-step the buyer is on; the context
  // sub-step hosts it over its own layout.
  if ((isAnalyzing || analysisFailed) && currentSubStep === 2) return analysisWait;

  // Journey-rail and pill clicks share the same id → sub-step mapping.
  const handleSubStepNav = (id: string) => {
    const subStep = SUB_STEP_IDS[id];
    if (subStep) dispatch(setCurrentSubStep(subStep));
  };

  // Render sub-step content
  const renderSubStepContent = () => {
    switch (currentSubStep) {
      case 1:
        return (
          <ContextStepPanel
            questions={contextQuestions}
            bomAnswers={bomAnswers}
            exactParts={exactParts}
            isAnalyzing={isAnalyzing || !analysisData}
            analysisFailed={analysisFailed}
            onRetryAnalysis={readOnly ? undefined : analysisRetry.retry}
            retryError={analysisRetry.retryError}
            longWait={analysisLong}
            progress={analysisRunProgress}
            onSubmit={async (answers) => {
              await fixes.submitContext(answers);
              dispatch(setCurrentSubStep(2));
            }}
            onBack={onPrevious}
            onContinue={() => dispatch(setCurrentSubStep(2))}
            onSubStepSelect={handleSubStepNav}
            handoff={handoff}
          />
        );
      case 2:
        // The validation panel auto-runs a check on mount when it sees no
        // results, and before the status fetch "no results" is not yet known
        // — so it is not mounted until the analysis is in hand. (A deep link
        // used to mount it bare, then again after the gate: two runs.)
        if (!analysisData) return analysisWait;
        return (
          <ValidationStep
            ruleResults={analysisData?.ruleResults || []}
            componentAlternatives={analysisData?.componentAlternatives || []}
            aiSuggestion={analysisData?.aiSuggestion || null}
            components={components}
            reviewStates={analysisData?.reviewStates || null}
            onPersistReviewState={fixes.persistReviewState}
            onRunValidation={fixes.runValidation}
            validationRun={analysisData?.validationRun}
            onAwaitValidation={fixes.awaitValidation}
            onRunningChange={setValidationRunning}
            onFailedChange={setValidationFailed}
            onReplaceComponent={fixes.replaceComponent}
            onRevertComponent={fixes.revertComponent}
            onAddComponent={fixes.addComponent}
            onApplySuggestion={fixes.applySuggestion}
            // A viewer's Continue only moves them along; kicking off supplier
            // research is a write, and it is the owner's to start.
            onNext={readOnly ? () => dispatch(setCurrentSubStep(3)) : discovery.checkpoint.show}
            onPrevious={() => dispatch(setCurrentSubStep(1))}
            isNextLoading={discovery.isStartingResearch}
            onSubStepSelect={handleSubStepNav}
            handoff={handoff}
          />
        );
      case 3:
        return (
          <SupplierDiscovery
            bomId={bomId || ''}
            components={components}
            onExportToExcel={discovery.exportToExcel}
            onRetryComponent={discovery.retryComponent}
            onSwapSpec={readOnly ? undefined : discovery.swapSpec}
            swappingSpec={discovery.swappingSpec}
            research={research}
            rfq={rfq}
            isExporting={discovery.isExporting}
            retryingComponents={discovery.retryingComponents}
            onPrevious={() => dispatch(setCurrentSubStep(2))}
            onNext={onNext}
            requirements={rfqData?.requirements || (rfq as any)?.requirements || undefined}
            onAskRobbie={onAskRobbie}
            onSubStepSelect={handleSubStepNav}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Smart BOM surfaces on the supplier-discovery sub-step ONLY: the
          reveal moment ("your BOM is now smarter than the one you uploaded")
          plus the quiet pill reference. Context/validation stay clean — the
          Smart BOM is earned through those steps, then revealed here. */}
      {currentSubStep === 3 && bomId && (
        <SmartBomDiscoveryPanel bomId={bomId} />
      )}

      <SmartBomCheckpoint
        bomId={bomId}
        open={discovery.checkpoint.open}
        error={discovery.checkpoint.error}
        onCancel={discovery.checkpoint.cancel}
        onProceed={discovery.checkpoint.proceed}
      />

      {/* Sub-Step Content */}
      {renderSubStepContent()}
      <ActionErrorToast state={discovery.actionError} />

    </div>
  );
};

export default Step2SmartBOMReview;
